import { useState, useCallback } from "react";
import { useQuery, useQueryClient } from "react-query";
import { format } from "date-fns";
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronLeft, ChevronRight as ChevronRightIcon } from "lucide-react";

const formatMoney = (v: number) => `${v?.toLocaleString()} FRW`;

const SOURCE_MODULES = [
  "contribution",
  "expense",
  "loan",
  "loan_repayment",
  "penalty",
  "interest_distribution",
] as const;

type SourceModule = (typeof SOURCE_MODULES)[number];

const STATUS_OPTIONS = ["Posted", "Reversed", "Void"] as const;

interface JournalEntry {
  id: number;
  journalNumber: string;
  journalDate: string;
  sourceModule: string;
  reference: string | null;
  narration: string;
  totalDebit: number;
  totalCredit: number;
  status: string;
  createdBy: {
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  lines: JournalLine[];
}

interface JournalLine {
  id: number;
  debitAccountCode: string;
  debitAccountName: string;
  creditAccountCode: string;
  creditAccountName: string;
  amount: number;
  description: string | null;
}

interface PaginatedResponse {
  results: JournalEntry[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export default function JournalEntries() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sourceModule, setSourceModule] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const handleSearch = useCallback((val: string) => {
    setSearch(val);
    const timeout = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(1);
    }, 400);
    return () => clearTimeout(timeout);
  }, []);

  const resetFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setSourceModule("");
    setStatusFilter("");
    setStartDate("");
    setEndDate("");
    setPage(1);
  };

  const hasActiveFilters = debouncedSearch || sourceModule || statusFilter || startDate || endDate;

  const { data, isLoading, isFetching } = useQuery<PaginatedResponse>(
    ["journals", debouncedSearch, sourceModule, statusFilter, startDate, endDate, page, pageSize],
    async () => {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (sourceModule) params.set("sourceModule", sourceModule);
      if (statusFilter) params.set("status", statusFilter);
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const { data } = await api.get(`/journals?${params.toString()}`);
      return data;
    },
    { keepPreviousData: true }
  );

  const journals: JournalEntry[] = data?.results || [];
  const pagination = data?.pagination || { page: 1, pageSize: 10, total: 0, totalPages: 1 };

  const toggleRow = (id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      Posted: "bg-emerald-50 text-emerald-700 border-emerald-200",
      Reversed: "bg-amber-50 text-amber-700 border-amber-200",
      Void: "bg-red-50 text-red-700 border-red-200",
    };
    return styles[status] || "bg-slate-50 text-slate-700 border-slate-200";
  };

  const formatModuleLabel = (mod: string) => {
    return mod
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  return (
    <div className="min-h-screen bg-gray-50/50">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Journal Entries</h1>
            <p className="text-sm text-slate-500 mt-1">
              Double-entry bookkeeping records for all financial transactions
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                queryClient?.invalidateQueries?.("journals");
              }}
              className="h-9 border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 shadow-sm">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-medium text-slate-500 mb-1 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by journal #, narration..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-9 h-9 bg-slate-50 border-slate-200"
                />
              </div>
            </div>
            <div className="min-w-[160px]">
              <label className="text-xs font-medium text-slate-500 mb-1 block">From Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                className="h-9 bg-slate-50 border-slate-200"
              />
            </div>
            <div className="min-w-[160px]">
              <label className="text-xs font-medium text-slate-500 mb-1 block">To Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                className="h-9 bg-slate-50 border-slate-200"
              />
            </div>
            <div className="min-w-[170px]">
              <label className="text-xs font-medium text-slate-500 mb-1 block">Source Module</label>
              <Select
                value={sourceModule}
                onValueChange={(val) => { setSourceModule(val === "all" ? "" : val); setPage(1); }}
              >
                <SelectTrigger className="h-9 bg-slate-50 border-slate-200">
                  <SelectValue placeholder="All Modules" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Modules</SelectItem>
                  {SOURCE_MODULES.map((mod) => (
                    <SelectItem key={mod} value={mod}>
                      {formatModuleLabel(mod)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[150px]">
              <label className="text-xs font-medium text-slate-500 mb-1 block">Status</label>
              <Select
                value={statusFilter}
                onValueChange={(val) => { setStatusFilter(val === "all" ? "" : val); setPage(1); }}
              >
                <SelectTrigger className="h-9 bg-slate-50 border-slate-200">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={resetFilters}
                className="h-9 border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                <X className="h-4 w-4 mr-1" />
                Reset
              </Button>
            )}
          </div>
        </div>

        {/* Journal Entries Table */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <div className="p-1.5 bg-emerald-100 rounded-lg">
                <BookOpen className="h-4 w-4 text-emerald-600" />
              </div>
              Journal Entries
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
              </div>
            ) : journals.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="p-4 bg-slate-100 rounded-full mb-4">
                  <BookOpen className="h-8 w-8 text-slate-400" />
                </div>
                <p className="text-sm font-medium text-slate-900">No journal entries found</p>
                <p className="text-xs text-slate-500 mt-1">
                  {hasActiveFilters ? "Try adjusting your filters" : "Journal entries will appear here as transactions are posted"}
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-emerald-600 hover:bg-emerald-600">
                        <TableHead className="text-xs font-semibold text-white w-8" />
                        <TableHead className="text-xs font-semibold text-white">Journal #</TableHead>
                        <TableHead className="text-xs font-semibold text-white">Date</TableHead>
                        <TableHead className="text-xs font-semibold text-white">Source Module</TableHead>
                        <TableHead className="text-xs font-semibold text-white">Reference</TableHead>
                        <TableHead className="text-xs font-semibold text-white">Narration</TableHead>
                        <TableHead className="text-xs font-semibold text-white text-right">Debit (FRW)</TableHead>
                        <TableHead className="text-xs font-semibold text-white text-right">Credit (FRW)</TableHead>
                        <TableHead className="text-xs font-semibold text-white">Status</TableHead>
                        <TableHead className="text-xs font-semibold text-white">Created By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {journals.map((journal) => (
                        <>
                          <TableRow
                            key={journal.id}
                            className="hover:bg-slate-50/50 border-b border-slate-100 cursor-pointer"
                            onClick={() => toggleRow(journal.id)}
                          >
                            <TableCell className="w-8">
                              {expandedRows.has(journal.id) ? (
                                <ChevronDown className="h-4 w-4 text-slate-400" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-slate-400" />
                              )}
                            </TableCell>
                            <TableCell>
                              <span className="text-xs font-mono bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded border border-emerald-200">
                                {journal.journalNumber}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm text-slate-700">
                              {journal.journalDate
                                ? format(new Date(journal.journalDate), "MMM dd, yyyy")
                                : "—"}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className="text-xs font-medium border-0 bg-blue-50 text-blue-700"
                              >
                                {formatModuleLabel(journal.sourceModule)}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-600 font-mono text-xs">
                              {journal.reference || "—"}
                            </TableCell>
                            <TableCell className="text-sm text-slate-700 max-w-[250px] truncate">
                              {journal.narration || "—"}
                            </TableCell>
                            <TableCell className="text-sm font-semibold text-emerald-600 text-right">
                              {formatMoney(journal.totalDebit)}
                            </TableCell>
                            <TableCell className="text-sm font-semibold text-rose-600 text-right">
                              {formatMoney(journal.totalCredit)}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={`text-xs font-medium border ${getStatusBadge(journal.status)}`}
                              >
                                {journal.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">
                              {journal.createdBy
                                ? `${journal.createdBy.firstName} ${journal.createdBy.lastName || ""}`.trim()
                                : "—"}
                            </TableCell>
                          </TableRow>

                          {/* Expanded Lines */}
                          {expandedRows.has(journal.id) && journal.lines?.length > 0 && (
                            <TableRow key={`${journal.id}-lines`} className="bg-slate-50/80">
                              <TableCell colSpan={10} className="p-0">
                                <div className="px-6 py-3 border-l-4 border-emerald-400 ml-4">
                                  <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">
                                    Journal Lines
                                  </p>
                                  <Table>
                                    <TableHeader>
                                      <TableRow className="bg-slate-100 hover:bg-slate-100">
                                        <TableHead className="text-xs font-semibold text-slate-600 h-8">
                                          Debit Account
                                        </TableHead>
                                        <TableHead className="text-xs font-semibold text-slate-600 h-8">
                                          Credit Account
                                        </TableHead>
                                        <TableHead className="text-xs font-semibold text-slate-600 h-8 text-right">
                                          Amount (FRW)
                                        </TableHead>
                                        <TableHead className="text-xs font-semibold text-slate-600 h-8">
                                          Description
                                        </TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {journal.lines.map((line) => (
                                        <TableRow key={line.id} className="hover:bg-white/50">
                                          <TableCell className="text-xs">
                                            <span className="font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                              {line.debitAccountCode}
                                            </span>{" "}
                                            <span className="text-slate-600">{line.debitAccountName}</span>
                                          </TableCell>
                                          <TableCell className="text-xs">
                                            <span className="font-mono text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded">
                                              {line.creditAccountCode}
                                            </span>{" "}
                                            <span className="text-slate-600">{line.creditAccountName}</span>
                                          </TableCell>
                                          <TableCell className="text-xs font-semibold text-slate-900 text-right">
                                            {formatMoney(line.amount)}
                                          </TableCell>
                                          <TableCell className="text-xs text-slate-500">
                                            {line.description || "—"}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                          {expandedRows.has(journal.id) && (!journal.lines || journal.lines.length === 0) && (
                            <TableRow key={`${journal.id}-empty`} className="bg-slate-50/80">
                              <TableCell colSpan={10} className="text-center py-4 text-xs text-slate-400">
                                No journal lines available
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                  <p className="text-xs text-slate-500">
                    Showing {(page - 1) * pageSize + 1} to{" "}
                    {Math.min(page * pageSize, pagination.total)} of {pagination.total} entries
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="h-8 w-8 p-0 border-slate-200"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                      const startPage = Math.max(1, Math.min(page - 2, pagination.totalPages - 4));
                      const pageNum = startPage + i;
                      if (pageNum > pagination.totalPages) return null;
                      return (
                        <Button
                          key={pageNum}
                          variant={pageNum === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => setPage(pageNum)}
                          className={`h-8 w-8 p-0 text-xs ${
                            pageNum === page
                              ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                              : "border-slate-200"
                          }`}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                      disabled={page >= pagination.totalPages}
                      className="h-8 w-8 p-0 border-slate-200"
                    >
                      <ChevronRightIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
